// Admin → System → Support. UI variant of `orbital diag` for
// customer-ops engineers without shell access (item 100 Phase B of
// PRODUCT-ROADMAP.md, item 57 Phase B in the original CLI item).
//
// Form posts to /api/admin/support/generate-bundle (Phase A) and
// drops the returned tar.gz onto disk via the standard blob+anchor
// download dance. The page deliberately stays read+download-only —
// no bundle preview, no upload-to-portal — so an admin can't trigger
// an expensive bundle write through a one-click button on the wrong
// install. Customer attaches the file manually to the customer-portal
// support form (item 101) once it lands locally.
//
// `since_hours` is hard-clamped server-side at 7 × 24; the form's
// quick-select set ranges max out at 7 days so the input matches
// what the handler will actually accept.

import { useState } from 'react';
import { LifeBuoy, Download, AlertTriangle, ExternalLink } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  SUPPORT_SECTIONS,
  generateSupportBundle,
  type SupportSection,
} from '../../api/support';

interface WindowOption {
  value: number;        // hours
  label: string;
}

const WINDOW_OPTIONS: WindowOption[] = [
  { value: 1,   label: 'Last hour' },
  { value: 24,  label: 'Last 24 hours' },
  { value: 72,  label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' },
];

const SECTION_LABELS: Record<SupportSection, string> = {
  logs:           'Recent failures & logs',
  metrics:        'Metrics snapshot',
  config:         'Feature flags & config',
  deployment:     'Deployment manifest',
  'db-stats':     'Database stats',
  observability: 'Replication status',
};

const SECTION_HINTS: Record<SupportSection, string> = {
  logs:           'Last N rows from the failure log; capped server-side.',
  metrics:        'Point-in-time RED + USE counters from the running process.',
  config:         'Feature-flag values and operator-supplied config (PII not stripped).',
  deployment:     'Version, build time, commit, runtime fingerprint.',
  'db-stats':     'Postgres table sizes + cardinalities for sizing triage.',
  observability: 'Replica lag and CNPG cluster posture if configured.',
};

export function AdminSupport() {
  const [sinceHours, setSinceHours] = useState<number>(24);
  const [included, setIncluded] = useState<Set<SupportSection>>(
    () => new Set(SUPPORT_SECTIONS),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);
  const [lastSize, setLastSize] = useState<number | null>(null);

  const toggle = (s: SupportSection) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const allOn = included.size === SUPPORT_SECTIONS.length;
  const noneOn = included.size === 0;

  const generate = async () => {
    if (noneOn) {
      setError('Select at least one section to include.');
      return;
    }
    setBusy(true);
    setError(null);
    setLastFile(null);
    setLastSize(null);
    try {
      const out = await generateSupportBundle({
        since_hours: sinceHours,
        include: Array.from(included),
      });
      setLastFile(out.filename);
      setLastSize(out.size);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <LifeBuoy className="h-5 w-5 text-nebula" />
          Support
        </span>
      }
      description="Generate a diagnostic bundle to attach to a vendor support escalation. Same data surface as the orbital diag CLI — just packaged in-process so an air-gapped or shell-less operator can pull it from the admin UI."
    >
      <div className="space-y-6 max-w-4xl">
        <Card>
          <CardHeader
            title="Bundle window"
            description="How far back recent-failures and time-windowed sections should reach. Hard-capped at 7 days server-side."
          />
          <CardBody className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSinceHours(opt.value)}
                  className={
                    sinceHours === opt.value
                      ? 'orb-card px-3 py-2 text-sm border-nebula/40 bg-nebula/10 text-nebula'
                      : 'orb-card px-3 py-2 text-sm hover:bg-bg-hover/40 text-fg-muted'
                  }
                  aria-pressed={sinceHours === opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Sections"
            description="Sections to capture. All on by default; uncheck anything you don't want shipped to support."
            actions={
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setIncluded(
                    allOn ? new Set() : new Set(SUPPORT_SECTIONS),
                  )
                }
              >
                {allOn ? 'Clear all' : 'Select all'}
              </Button>
            }
          />
          <CardBody className="space-y-2">
            {SUPPORT_SECTIONS.map((s) => {
              const on = included.has(s);
              return (
                <label
                  key={s}
                  className="flex items-start gap-3 px-3 py-2 rounded-md border border-border/60 hover:bg-bg-hover/40 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(s)}
                    className="mt-1 h-4 w-4 rounded border-border accent-nebula"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-fg flex items-center gap-2">
                      {SECTION_LABELS[s]}
                      <code className="text-xs font-mono text-fg-subtle">{s}</code>
                    </div>
                    <p className="text-xs text-fg-muted mt-0.5">{SECTION_HINTS[s]}</p>
                  </div>
                </label>
              );
            })}
          </CardBody>
          <CardFooter className="flex items-center justify-between gap-3">
            <p className="text-xs text-fg-subtle">
              {included.size} of {SUPPORT_SECTIONS.length} selected
            </p>
            <Button
              variant="primary"
              disabled={busy || noneOn}
              onClick={generate}
            >
              <Download className="h-4 w-4" />
              {busy ? 'Generating…' : 'Generate support bundle'}
            </Button>
          </CardFooter>
        </Card>

        <Card className="border-nebula/20 bg-nebula/5">
          <CardBody className="flex items-start gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-nebula mt-0.5 shrink-0" />
            <div className="text-fg-muted leading-relaxed">
              <p className="text-fg font-medium mb-1">Bundle is not auto-shared</p>
              <p>
                The file lands on your machine. To deliver it, attach it to your
                vendor support ticket — the customer portal accepts the same
                filename shape that <code className="font-mono text-xs">orbital diag</code>{' '}
                produces.
              </p>
              <a
                href="https://portal.orbitalreg.com/support/new"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-nebula hover:underline"
              >
                Open customer portal support form
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardBody>
        </Card>

        {lastFile && (
          <Card className="border-success/30 bg-success/5">
            <CardBody className="flex items-start justify-between gap-4">
              <div>
                <Badge tone="success">Downloaded</Badge>
                <p className="font-mono text-sm text-fg mt-2 break-all">{lastFile}</p>
                {lastSize !== null && (
                  <p className="text-xs text-fg-muted mt-1">
                    {fmtBytes(lastSize)} · application/gzip
                  </p>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {error && (
          <Card className="border-danger/30 bg-danger/5">
            <CardBody>
              <p className="text-danger font-mono text-sm break-all">{error}</p>
            </CardBody>
          </Card>
        )}
      </div>
    </AdminShell>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
}
